import { type FC, useState } from "react";

import { CourseCodeMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { TextField } from "mds/components/TextField";
import { storeApi } from "store/index";
import { toastSuccessMessage } from "utils/alerts";

type AddCourseModalProps = {
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  onClose: () => void;
  defaultCourseCodeId?: string | null;
  defaultTermId?: string | null;
};

export const AddCourseModal: FC<AddCourseModalProps> = ({
  eventName,
  eventProperties,
  onClose,
  defaultCourseCodeId,
  defaultTermId,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTermId, setSelectedTermId] = useState<string>(defaultTermId || "");
  const [selectedCourseCodeId, setSelectedCourseCodeId] = useState<string>(
    defaultCourseCodeId || "",
  );

  const handleSubmit = () => {
    if (!title.trim() || !selectedCourseCodeId) {
      return;
    }

    storeApi.courses
      .create({
        course_id: selectedCourseCodeId,
        description: description.trim(),
        term_id: selectedTermId || undefined,
        title: title.trim(),
      })
      .then(() => {
        toastSuccessMessage(t("home.toasts.course_added"));
        onClose();
      });
  };

  const handleTermChange = (termId: string) => {
    setSelectedTermId(termId);
  };

  const handleCourseCodeChange = (courseCodeId: string) => {
    setSelectedCourseCodeId(courseCodeId);
  };

  return (
    <div className="mt-8 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div className="font-medium">{t("home.course_title_label")}</div>
          <TextField
            eventName={eventName}
            eventProperties={eventProperties}
            placeholder={t("home.placeholder.course_title")}
            value={title}
            isEditing
            onChange={(e) => setTitle(String(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && title.trim() && selectedCourseCodeId && selectedTermId) {
                handleSubmit();
              }
            }}
          />
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div className="font-medium">{t("home.course_description_label")}</div>
          <TextField
            eventName={eventName}
            eventProperties={eventProperties}
            placeholder={t("home.placeholder.course_description")}
            value={description}
            isEditing
            onChange={(e) => setDescription(String(e.target.value))}
          />
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <div className="flex flex-1 flex-col gap-2">
            <div className="font-medium">{t("home.course_codes.select_course_code_label")}</div>
            <CourseCodeMenu
              defaultToAll={false}
              menuClassName="max-h-344"
              menuType="menu"
              selectedCourseCodeId={selectedCourseCodeId}
              onCourseCodeChange={handleCourseCodeChange}
            />
          </div>

          <div className="flex flex-1 flex-col gap-2">
            <div className="font-medium">{t("home.terms.select_term_label")}</div>
            <TermMenu
              defaultToAll={false}
              menuClassName="max-h-344"
              menuType="menu"
              selectedTermId={selectedTermId}
              onTermChange={handleTermChange}
            />
          </div>
        </div>
      </div>

      <div className="flex w-full justify-end gap-2">
        <Button kind="white" size="s" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button
          disabled={!title.trim() || !selectedCourseCodeId || !selectedTermId}
          kind="blue"
          size="s"
          title={t("common.add.course")}
          onClick={handleSubmit}
        >
          {t("common.add.course")}
        </Button>
      </div>
    </div>
  );
};
